import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { UserPlus, Shield, User as UserIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const describeError = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return err?.message || 'Something went wrong. Please try again.';
};

// Admin "Add User" dialog: creates the account with an auto-generated temporary
// PIN. Nothing is emailed here — the admin sends the welcome from the row.
export const AddUserDialog = ({ open, onClose, authToken, onCreated }) => {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [groups, setGroups] = useState([]);
  const [newGroup, setNewGroup] = useState('');
  const [available, setAvailable] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setEmail(''); setDisplayName(''); setIsAdmin(false); setGroups([]); setNewGroup(''); setError(''); setResult(null);
    axios.get(`${API_URL}/api/admin/groups`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((r) => setAvailable(((r.data && r.data.groups) || []).map((g) => g.name).filter(Boolean)))
      .catch(() => setAvailable([]));
  }, [open, authToken]);

  const toggleGroup = (g) => setGroups((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));

  const addNewGroup = () => {
    const g = newGroup.trim();
    if (!g) return;
    if (!available.includes(g)) setAvailable((prev) => [...prev, g]);
    if (!groups.includes(g)) setGroups((prev) => [...prev, g]);
    setNewGroup('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/users`,
        { email: email.trim(), display_name: displayName.trim(), is_admin: isAdmin, groups: groups.join(',') },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setResult(r.data);
      onCreated && onCreated(r.data);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  const field = { width: '100%', padding: '0.55rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.9rem' };
  const label = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-user-dialog">
        <DialogHeader>
          <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <UserPlus size={18} /> Add User
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <div data-testid="add-user-result">
            <p style={{ margin: '0 0 0.6rem' }}>✅ <strong>{displayName}</strong> was created as {result.is_admin ? 'an Admin' : 'a User'}.</p>
            <div style={{ background: '#e8f1e8', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '0.8rem 1rem', marginBottom: '0.8rem' }}>
              <div style={{ fontSize: '0.75rem', color: '#475569' }}>Temporary PIN (shown once)</div>
              <div style={{ fontFamily: 'monospace', fontSize: '1.6rem', fontWeight: 700, letterSpacing: '4px', color: '#2c5f2d' }} data-testid="add-user-temp-pin">{result.temporary_pin}</div>
            </div>
            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0 0 0.8rem' }}>
              Nothing has been emailed yet. Use <strong>✉️ Welcome</strong> on their row to send the welcome letter — it will include a fresh temporary PIN.
            </p>
            <button type="button" className="hide-emails-btn" onClick={onClose} data-testid="add-user-done-btn">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'grid', gap: '0.8rem' }}>
            <div>
              <label style={label}>Email</label>
              <input style={field} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" data-testid="add-user-email" />
            </div>
            <div>
              <label style={label}>Display name</label>
              <input style={field} type="text" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="How their name appears in the app" data-testid="add-user-display-name" />
            </div>
            <div>
              <label style={label}>Role</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[{ v: false, t: 'User', I: UserIcon }, { v: true, t: 'Admin', I: Shield }].map(({ v, t, I }) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setIsAdmin(v)}
                    data-testid={`add-user-role-${t.toLowerCase()}`}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem',
                      padding: '0.5rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                      border: `2px solid ${isAdmin === v ? '#2c5f2d' : '#e2e8f0'}`,
                      background: isAdmin === v ? '#e8f1e8' : '#fff', color: isAdmin === v ? '#2c5f2d' : '#475569',
                    }}
                  >
                    <I size={14} /> {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label style={label}>Groups <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.5rem' }} data-testid="add-user-groups">
                {available.length === 0 && <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>No groups yet — add one below.</span>}
                {available.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => toggleGroup(g)}
                    data-testid={`add-user-group-${g}`}
                    style={{
                      padding: '0.25rem 0.7rem', borderRadius: '999px', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600,
                      border: `1px solid ${groups.includes(g) ? '#2c5f2d' : '#cbd5e1'}`,
                      background: groups.includes(g) ? '#2c5f2d' : '#f8fafc', color: groups.includes(g) ? '#fff' : '#475569',
                    }}
                  >
                    {g}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <input style={{ ...field, flex: 1 }} type="text" value={newGroup} onChange={(e) => setNewGroup(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNewGroup(); } }} placeholder="New group name" data-testid="add-user-new-group" />
                <button type="button" className="hide-emails-btn" onClick={addNewGroup} data-testid="add-user-add-group-btn">Add</button>
              </div>
            </div>
            {error && <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '6px', padding: '0.5rem 0.7rem', fontSize: '0.85rem' }} data-testid="add-user-error">{error}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.3rem' }}>
              <button type="button" onClick={onClose} style={{ ...field, width: 'auto', background: '#fff', cursor: 'pointer' }} data-testid="add-user-cancel-btn">Cancel</button>
              <button type="submit" className="hide-emails-btn" disabled={saving} data-testid="add-user-submit-btn">{saving ? 'Creating…' : 'Create User'}</button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddUserDialog;
